/**
 * Pure helpers backing CopilotChatPanel.js's "persist before touching React
 * state" pattern (see ASTT-7496 root cause #1). Extracted out of the
 * component so the actual merge/discard decision -- the part that matters
 * for the conversation-state-desync fix -- can be unit tested directly,
 * without mounting the component, mocking axios, or driving a real browser
 * close/reopen cycle (impractical to automate outside a live Dev
 * environment). CopilotChatPanel.js is the ONLY caller of these -- it still
 * owns all persistence (saveConversation) and React state; this module just
 * decides WHAT the resulting messages array should be.
 */

/** Identical to what CopilotChatPanel.js used to define inline -- moved
 * here so both the component and this module's own tests share one
 * implementation instead of two copies that could drift. Backward-compat
 * for sessionStorage conversations saved before multi-variant (Regenerate
 * pagination) support: those stored agent messages are a flat shape with no
 * `variants` array, which every render/handler now expects. */
export function normalizeMessages(rawMessages) {
  return rawMessages.map((m) => {
    if (m.role !== "agent" || Array.isArray(m.variants)) return m;
    const { text, path, sources, followups, logId, durationMs, feedback, ...rest } = m;
    return {
      ...rest,
      role: "agent",
      variants: [{ text, path, sources, followups, logId, durationMs, feedback }],
      activeVariantIndex: 0,
    };
  });
}

/**
 * Decides whether a completed agent response still belongs to the
 * conversation it was asked in, and if so, which message array to build the
 * update on top of.
 *
 * - requestSessionId: sessionId captured BEFORE the request was sent.
 * - currentSessionId: sessionIdRef.current AT COMPLETION TIME -- if this
 *   differs from requestSessionId, the user has since started a new
 *   conversation (sessionIdRef.current changes on "New conversation") and
 *   there's no valid place left to put a stale answer for the abandoned
 *   session.
 * - stored: whatever loadConversation() returns right now (fresh from
 *   sessionStorage, not a closed-over React value) -- may be null (nothing
 *   saved yet this session) or may belong to a DIFFERENT session than
 *   requestSessionId if the panel was closed/reopened or "New conversation"
 *   was clicked while this request was in flight.
 * - fallbackMessages: the component's own messagesRef.current, used only
 *   when `stored` is null.
 *
 * Returns { discard: true } if the response should be silently dropped, or
 * { discard: false, baseMessages } with the array to build on top of.
 */
export function resolveMergeTarget({ requestSessionId, currentSessionId, stored, fallbackMessages }) {
  const isStillCurrentSession =
    currentSessionId === requestSessionId && (stored == null || stored.sessionId === requestSessionId);

  if (!isStillCurrentSession) {
    return { discard: true, baseMessages: null };
  }

  const baseMessages = stored ? normalizeMessages(stored.messages) : fallbackMessages;
  return { discard: false, baseMessages };
}

/** Appends a fresh agent message (a normal question's answer) onto
 * baseMessages. Pure -- returns a new array, never mutates the input. */
export function appendAgentMessage(baseMessages, variant) {
  return [...baseMessages, { role: "agent", variants: [variant], activeVariantIndex: 0 }];
}

/**
 * Appends a new variant onto an EXISTING agent message at `idx` (a
 * Regenerate attempt). Returns null if `idx` no longer points at an agent
 * message in baseMessages -- defensive; see CopilotChatPanel.js's
 * handleRegenerate remarks on why this shouldn't happen in practice, but
 * costs nothing to guard against.
 */
export function applyRegenerateVariant(baseMessages, idx, variant) {
  const target = baseMessages[idx];
  if (!target || target.role !== "agent") return null;

  const variants = [...target.variants, variant];
  const updated = [...baseMessages];
  updated[idx] = { ...target, variants, activeVariantIndex: variants.length - 1 };
  return updated;
}
